import type { Member } from '../types';

const bufToHex = (buf: ArrayBuffer) =>
  Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

const randomSaltHex = () => {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return bufToHex(arr.buffer);
};

async function sha256Hex(text: string) {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return bufToHex(hash);
}

async function pbkdf2Hex(password: string, saltHex: string) {
  const enc = new TextEncoder();
  const salt = new Uint8Array(saltHex.match(/.{2}/g)!.map((h) => parseInt(h, 16)));
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: 150000, hash: 'SHA-256' },
    keyMaterial,
    256
  );
  return bufToHex(bits);
}

export async function makePasswordFields(plain: string) {
  const salt = randomSaltHex();
  const passwordHash = await pbkdf2Hex(plain, salt);
  return { salt, passwordHash };
}

export async function verifyPassword(member: Member, plain: string) {
  if (!member.passwordHash) return false;
  if (member.salt) {
    return (await pbkdf2Hex(plain, member.salt)) === member.passwordHash;
  }
  return (await sha256Hex(plain)) === member.passwordHash;
}
